import * as React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Clock as ClockIcon, Question as QuestionIcon } from '@phosphor-icons/react/dist/ssr';

import { paths } from '@/paths';
import { RouterLink } from '@/components/core/v2/link';
import ProfessionalDialog from '@/components/common/professional-dialog';

import { getArticleBySlug } from '../data/help-articles';
import { useHelpAccess } from '../use-help-access';
import { HelpArticleContent } from './help-article-content';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
  /** The guide to open initially; falls back to a "browse all" prompt when absent or inaccessible. */
  slug?: string;
}

/**
 * Shows a help guide inline in a dialog so users keep their place instead of
 * navigating away mid-task. Related guides swap the dialog content in place.
 */
export function HelpDialog({ open, onClose, slug }: HelpDialogProps): React.JSX.Element {
  const { canSeeArticle } = useHelpAccess();
  const [activeSlug, setActiveSlug] = React.useState<string | undefined>(slug);

  // Reset to the page's contextual guide each time the dialog is (re)opened.
  React.useEffect(() => {
    if (open) setActiveSlug(slug);
  }, [open, slug]);

  const article = activeSlug ? getArticleBySlug(activeSlug) : undefined;
  const accessible = article ? canSeeArticle(article) : false;
  const Icon = article && accessible ? article.icon : QuestionIcon;

  return (
    <ProfessionalDialog
      actions={
        <>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }} variant="outlined">
            Close
          </Button>
          <Button
            component={RouterLink}
            href={paths.help.index}
            onClick={onClose}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            variant="contained"
          >
            Open help center
          </Button>
        </>
      }
      icon={<Icon size={20} weight="duotone" />}
      maxWidth="md"
      onClose={onClose}
      open={open}
      subtitle={article && accessible ? article.summary : 'Step-by-step guides for getting things done'}
      title={article && accessible ? article.title : 'Help'}
    >
      {article && accessible ? (
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={`${article.steps.length} steps`} size="small" variant="outlined" />
            {article.estimatedMinutes ? (
              <Chip
                icon={<ClockIcon size={14} />}
                label={`About ${article.estimatedMinutes} min`}
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>
          <HelpArticleContent article={article} canSeeArticle={canSeeArticle} onSelectRelated={setActiveSlug} />
        </Stack>
      ) : (
        <Box sx={{ py: 1 }}>
          <Typography color="text.secondary" variant="body2">
            We don&apos;t have a specific guide for this page yet. Open the help center to browse all guides and take a
            product tour.
          </Typography>
        </Box>
      )}
    </ProfessionalDialog>
  );
}
